import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, MessageSquare, ThumbsUp, ThumbsDown, Globe, UserCheck, AlertCircle } from 'lucide-react';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../config/firebase';
import { toISOString } from '../utils/firebaseHelpers';
import { checkRateLimit } from '../utils/rateLimiter';

interface PlatformFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: any;
  userId: string;
  onSuccess: () => void;
}

const PLATFORM_ASPECTS = [
  { key: 'websiteUI', label: 'Website/App UI' },
  { key: 'bookingProcess', label: 'Booking Process' },
  { key: 'paymentExperience', label: 'Payment Experience' },
  { key: 'customerSupport', label: 'Customer Support' },
  { key: 'communication', label: 'Communication' }
];

const PROVIDER_ASPECTS = [
  { key: 'serviceQuality', label: 'Service Quality' },
  { key: 'professionalism', label: 'Professionalism' },
  { key: 'punctuality', label: 'Punctuality' },
  { key: 'cleanliness', label: 'Cleanliness' },
  { key: 'valueForMoney', label: 'Value for Money' }
];

export default function PlatformFeedbackModal({
  isOpen,
  onClose,
  order,
  userId,
  onSuccess
}: PlatformFeedbackModalProps) {
  const [activeTab, setActiveTab] = useState<'website' | 'provider'>('website');
  
  // Website feedback state
  const [websiteRating, setWebsiteRating] = useState(0);
  const [websiteAspectRatings, setWebsiteAspectRatings] = useState<Record<string, number>>({});
  const [websiteFeedbackText, setWebsiteFeedbackText] = useState('');
  const [websiteWouldRecommend, setWebsiteWouldRecommend] = useState<boolean | null>(null);
  
  // Service Provider feedback state
  const [providerRating, setProviderRating] = useState(0);
  const [providerAspectRatings, setProviderAspectRatings] = useState<Record<string, number>>({});
  const [providerFeedbackText, setProviderFeedbackText] = useState('');
  const [providerWouldRecommend, setProviderWouldRecommend] = useState<boolean | null>(null);
  
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (activeTab === 'website' && websiteRating === 0) {
      alert('Please provide a rating for the website experience');
      return;
    }
    
    if (activeTab === 'provider' && providerRating === 0) {
      alert('Please provide a rating for the service provider');
      return;
    }

    // Rate limiting check
    const rateLimitCheck = checkRateLimit(userId, 'FEEDBACK_FORM');
    if (!rateLimitCheck.allowed) {
      alert(rateLimitCheck.message || 'Too many feedback submissions. Please wait.');
      return;
    }

    try {
      setSubmitting(true);

      if (activeTab === 'website') {
        // Website Feedback - stores in 'feedback' collection
        const websiteFeedbackData = {
          userId: userId,
          userName: order.customerName || 'Anonymous',
          userEmail: order.customerEmail || '',
          orderId: order.id,
          serviceName: order.serviceName || '',
          rating: websiteRating,
          aspectRatings: websiteAspectRatings,
          feedbackText: websiteFeedbackText.trim(),
          wouldRecommend: websiteWouldRecommend,
          type: 'platform',
          source: 'website',
          status: 'submitted',
          createdAt: toISOString(new Date()),
          timestamp: toISOString(new Date())
        };

        await addDoc(collection(db, 'feedback'), websiteFeedbackData);
        alert('Thank you for your website feedback!');
      } else {
        // Service Provider Feedback - stores in 'serviceProviderFeedbacks' collection
        const providerId = order.acceptedBy || order.serviceProviderId || order.providerId || '';
        
        if (!providerId) {
          alert('No service provider information found for this order. Please contact support.');
          setSubmitting(false);
          return;
        }
        
        const providerFeedbackData = {
          userId: userId,
          userName: order.customerName || order.userName || 'Anonymous',
          userEmail: order.customerEmail || order.userEmail || '',
          customerId: userId,
          orderId: order.id,
          serviceName: order.serviceName || order.service?.name || '',
          providerId: providerId,
          providerName: order.providerName || order.serviceProviderName || 'Service Provider',
          rating: providerRating,
          aspectRatings: providerAspectRatings,
          serviceQuality: providerAspectRatings.serviceQuality || 0,
          professionalism: providerAspectRatings.professionalism || 0,
          punctuality: providerAspectRatings.punctuality || 0,
          cleanliness: providerAspectRatings.cleanliness || 0,
          valueForMoney: providerAspectRatings.valueForMoney || 0,
          feedbackText: providerFeedbackText.trim(),
          wouldRecommend: providerWouldRecommend,
          type: 'provider',
          source: 'website',
          status: 'submitted',
          createdAt: toISOString(new Date()),
          timestamp: toISOString(new Date())
        };

        await addDoc(collection(db, 'serviceProviderFeedbacks'), providerFeedbackData);
        alert('Thank you for your service provider feedback!');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ 
    value, 
    onChange, 
    label 
  }: { 
    value: number; 
    onChange: (val: number) => void; 
    label?: string;
  }) => (
    <div className="flex items-center gap-2">
      {label && <span className="text-sm text-gray-600 min-w-[160px]">{label}</span>}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => label ? null : setHoveredRating(star)}
            onMouseLeave={() => label ? null : setHoveredRating(0)}
            className="transition-transform hover:scale-110 focus:outline-none"
          >
            <Star
              className={`h-5 w-5 ${
                star <= (label ? value : (hoveredRating || value))
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 z-10 rounded-t-xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold">Share Your Feedback</h2>
                <p className="text-sm text-purple-100 mt-1">Help us improve our services</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('website')}
                className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'website'
                    ? 'bg-white text-purple-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <Globe className="h-4 w-4" />
                Website Feedback
              </button>
              <button
                onClick={() => setActiveTab('provider')}
                className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                  activeTab === 'provider'
                    ? 'bg-white text-purple-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <UserCheck className="h-4 w-4" />
                Service Provider
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {activeTab === 'website' ? (
              <>
                {/* Website Overall Rating */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Overall Experience with RapsApp <span className="text-red-500">*</span>
                  </label>
                  <div className="flex justify-center">
                    <StarRating value={websiteRating} onChange={setWebsiteRating} />
                  </div>
                  {websiteRating > 0 && (
                    <p className="text-center text-sm text-gray-600 mt-2">
                      {websiteRating === 5 && '🌟 Outstanding!'}
                      {websiteRating === 4 && '👍 Great Experience'}
                      {websiteRating === 3 && '😊 Satisfactory'}
                      {websiteRating === 2 && '😐 Needs Improvement'}
                      {websiteRating === 1 && '😞 Poor Experience'}
                    </p>
                  )}
                </div>

                {/* Platform Aspects */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Rate Platform Features
                  </label>
                  <div className="space-y-3 bg-purple-50 rounded-lg p-4">
                    {PLATFORM_ASPECTS.map(aspect => (
                      <StarRating
                        key={aspect.key}
                        value={websiteAspectRatings[aspect.key] || 0}
                        onChange={(val) => setWebsiteAspectRatings(prev => ({ ...prev, [aspect.key]: val }))}
                        label={aspect.label}
                      />
                    ))}
                  </div>
                </div>

                {/* Would Recommend */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Would you recommend RapsApp to others?
                  </label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setWebsiteWouldRecommend(true)}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                        websiteWouldRecommend === true
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      <ThumbsUp className={`h-5 w-5 ${websiteWouldRecommend === true ? 'fill-green-500' : ''}`} />
                      Yes, I would!
                    </button>
                    <button
                      onClick={() => setWebsiteWouldRecommend(false)}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                        websiteWouldRecommend === false
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-300 hover:border-red-300'
                      }`}
                    >
                      <ThumbsDown className={`h-5 w-5 ${websiteWouldRecommend === false ? 'fill-red-500' : ''}`} />
                      Not really
                    </button>
                  </div>
                </div>

                {/* Feedback Text */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Additional Comments (Optional)
                  </label>
                  <textarea
                    value={websiteFeedbackText}
                    onChange={(e) => setWebsiteFeedbackText(e.target.value)}
                    placeholder="Tell us about your experience with our platform - website, booking process, payments, support, or anything else..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                    rows={4}
                  />
                </div>
              </>
            ) : (
              <>
                {/* Service Provider Info */}
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">Service Provider</h3>
                  <p className="text-sm text-gray-700">
                    {order.providerName || order.serviceProviderName || 'Service Provider'}
                  </p>
                  {order.serviceName && (
                    <p className="text-xs text-gray-600 mt-1">
                      Service: {order.serviceName}
                    </p>
                  )}
                  {!(order.acceptedBy || order.serviceProviderId || order.providerId) && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Provider not yet assigned to this order
                    </p>
                  )}
                </div>

                {/* Provider Overall Rating */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Overall Service Provider Rating <span className="text-red-500">*</span>
                  </label>
                  <div className="flex justify-center">
                    <StarRating value={providerRating} onChange={setProviderRating} />
                  </div>
                  {providerRating > 0 && (
                    <p className="text-center text-sm text-gray-600 mt-2">
                      {providerRating === 5 && '🌟 Excellent Service!'}
                      {providerRating === 4 && '👍 Good Job'}
                      {providerRating === 3 && '😊 Average'}
                      {providerRating === 2 && '😐 Below Average'}
                      {providerRating === 1 && '😞 Poor Service'}
                    </p>
                  )}
                </div>

                {/* Provider Aspects */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Rate Service Aspects
                  </label>
                  <div className="space-y-3 bg-indigo-50 rounded-lg p-4">
                    {PROVIDER_ASPECTS.map(aspect => (
                      <StarRating
                        key={aspect.key}
                        value={providerAspectRatings[aspect.key] || 0}
                        onChange={(val) => setProviderAspectRatings(prev => ({ ...prev, [aspect.key]: val }))}
                        label={aspect.label}
                      />
                    ))}
                  </div>
                </div>

                {/* Would Recommend Provider */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Would you recommend this service provider?
                  </label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setProviderWouldRecommend(true)}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                        providerWouldRecommend === true
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 hover:border-green-300'
                      }`}
                    >
                      <ThumbsUp className={`h-5 w-5 ${providerWouldRecommend === true ? 'fill-green-500' : ''}`} />
                      Yes, I would!
                    </button>
                    <button
                      onClick={() => setProviderWouldRecommend(false)}
                      className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                        providerWouldRecommend === false
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-300 hover:border-red-300'
                      }`}
                    >
                      <ThumbsDown className={`h-5 w-5 ${providerWouldRecommend === false ? 'fill-red-500' : ''}`} />
                      Not really
                    </button>
                  </div>
                </div>

                {/* Provider Feedback Text */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Additional Comments (Optional)
                  </label>
                  <textarea
                    value={providerFeedbackText}
                    onChange={(e) => setProviderFeedbackText(e.target.value)}
                    placeholder="Share your experience with the service provider - quality of work, behavior, timeliness, etc..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                    rows={4}
                  />
                </div>
              </>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                disabled={submitting}
              >
                Skip for Now
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  submitting || 
                  (activeTab === 'website' ? websiteRating === 0 : providerRating === 0) ||
                  (activeTab === 'provider' && !(order.acceptedBy || order.serviceProviderId || order.providerId))
                }
                className={`flex-1 px-6 py-3 ${
                  activeTab === 'website'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                    : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700'
                } text-white rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg`}
                title={
                  activeTab === 'provider' && !(order.acceptedBy || order.serviceProviderId || order.providerId)
                    ? 'Provider not yet assigned to this order'
                    : (activeTab === 'website' ? websiteRating === 0 : providerRating === 0)
                    ? 'Please provide a rating first'
                    : ''
                }
              >
                {submitting ? 'Submitting...' : `Submit ${activeTab === 'website' ? 'Website' : 'Provider'} Feedback`}
              </button>
            </div>
            
            {/* Help Text */}
            {activeTab === 'provider' && providerRating === 0 && (
              <p className="text-center text-sm text-gray-500 mt-2">
                ⭐ Please rate the service provider to enable submit button
              </p>
            )}
            {activeTab === 'website' && websiteRating === 0 && (
              <p className="text-center text-sm text-gray-500 mt-2">
                ⭐ Please rate your website experience to enable submit button
              </p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
